"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ROUTES } from "@/constants/routes";
import {
  clearActiveContentModalContext,
  getContentModalClosePath,
  getCurrentRelativePath,
  getHistoryContentModalContext,
  isContentDetailPath,
  openContentModalHistory,
  replaceContentModalHistoryOnClose,
  setActiveContentModalContext,
} from "@/utils/content-modal-navigation";

export default function useContentModalNavigation({
  isModalOpen = false,
  fallbackPath = ROUTES.current.play,
  onModalRouteEnter,
  onModalRouteExit,
} = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const isModalOpenRef = useRef(isModalOpen);
  const onModalRouteEnterRef = useRef(onModalRouteEnter);
  const onModalRouteExitRef = useRef(onModalRouteExit);

  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  useEffect(() => {
    onModalRouteEnterRef.current = onModalRouteEnter;
  }, [onModalRouteEnter]);

  useEffect(() => {
    onModalRouteExitRef.current = onModalRouteExit;
  }, [onModalRouteExit]);

  const openModalRoute = useCallback((options) => {
    return openContentModalHistory(options);
  }, []);

  const closeModalRoute = useCallback(() => {
    const currentPath = getCurrentRelativePath();
    const closePath = getContentModalClosePath({
      currentPath,
      fallbackPath,
    });

    clearActiveContentModalContext();

    if (isContentDetailPath(currentPath)) {
      replaceContentModalHistoryOnClose(closePath);
      return closePath;
    }

    router.push(closePath);
    return closePath;
  }, [fallbackPath, router]);

  useEffect(() => {
    const handlePopState = () => {
      const historyContext = getHistoryContentModalContext();

      if (historyContext?.renderMode === "modal") {
        setActiveContentModalContext(historyContext);
        onModalRouteEnterRef.current?.(historyContext);
        return;
      }

      if (!isModalOpenRef.current) return;

      clearActiveContentModalContext();
      onModalRouteExitRef.current?.();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    if (isContentDetailPath(getCurrentRelativePath())) return;

    clearActiveContentModalContext();
    onModalRouteExit?.();
  }, [isModalOpen, onModalRouteExit, pathname]);

  return {
    pathname,
    openModalRoute,
    closeModalRoute,
  };
}
